/**
 * Copyright 2024 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */
const colors = {
  white: '#F9F9F9',
  whiteWithOpacity: 'rgba(249, 249, 249, 0.5)',
  gray100: '#f8f9fa',
  gray200: '#ebebeb',
  gray300: '#dee2e6',
  gray400: '#ced4da',
  gray500: '#adb5bd',
  gray600: '#999',
  gray700: '#444',
  gray800: '#303030',
  gray900: '#222222',
  gray1000: '#1f1f1f',
  black: '#000000',

  blue: '#375a7f',
  indigo: '#6610f2',
  purple: '#6f42c1',
  pink: '#e83e8c',
  lightBlue: '#66b3ff',
  linkBlue: 'blue',
  red: '#EE2D52',
  lightRed: '#f70d1a',
  orange: '#fd7e14',
  yellow: '#F39C12',
  green: '#00bc8c',
  teal: '#20c997',
  cyan: '#3498DB',
  headerColor: '#2C2D37',
  transparent: 'transparent',
  blackWithOpacity: 'rgba(0, 0, 0, 0.95)',
  halfTransparentBlack: 'rgba(0, 0, 0, 0.5)'
};

export const paddings = {
  xsmall: '0.25rem',
  small: '0.5rem',
  medium: '1rem',
  large: '1.5rem',
  xlarge: '2rem'
};

export const theme = {
  footerHeight: '51px',
  primaryColor: colors.blue,
  secondaryColor: colors.gray700,
  successColor: colors.green,
  infoColor: colors.cyan,
  warningColor: colors.yellow,
  dangerColor: colors.red,
  secondaryLight: colors.gray800,
  secondaryDark: colors.gray500,
  headerAllowance: 200,
  primaryThemeColor: colors.lightBlue,

  primaryBackground: colors.gray900,

  linkColor: colors.green,
  blackWithOpacity: 'rgba(0, 0, 0, 0.95)',

  primaryFontSize: '0.9375rem',
  primaryLineHeight: '1.6rem',
  primaryFont: '\'Montserrat\', Helvetica, Arial, sans-serif',

  fontSizeXS: '0.65rem',
  fontSizeS: '0.8rem',
  fontSizeL: '1.2rem',
  fontSizeXl: '1.5rem',
  fontSizeXxl: '2rem',
  fontSizeXxxl: '2.5rem',
  fontSizeXxxxl: '3rem',

  primaryInputHeight: '2.2rem',
  formFieldWidth: 350,
  formFieldMaxWidth: 500,
  formFieldMaxHeight: 250,
  inputIconWidth: 16,

  primaryBorderColor: colors.gray400,
  primaryBorderWidth: '1px',
  primaryBorderRadius: '1.2rem',
  colors,

  loaderSize: {
    small: 12,
    medium: 20,
    large: 50
  },

  spacing: {
    xxSmall: '4px',
    xSmall: '8px',
    small: '12px',
    medium: '16px',
    large: '24px',
    xLarge: '48px',
    xxLarge: '64px'
  },

  screenSizes: {
    large: 1170,
    desktop: 992,
    tablet: 768,
    phone: 660,
    smallPhone: 360
  },
  mediaPhone: `@media only screen
    and (max-device-width: 737px)
    and (-webkit-min-device-pixel-ratio: 2)
  `,
  mediaLargeScreen: `@media only screen
    and (min-width: 1920px)
  `
};

export const pxToVw = (screenWidthInPixels: number, elementSizeInPixels: number): string => `${100 * elementSizeInPixels / screenWidthInPixels}vw`;
export const hexToRgba = (hex: string, opacityPercentage: number): string => {
  const rgbParsed = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (!rgbParsed) {
    return null;
  }

  const rgb = rgbParsed ? {
    r: parseInt(rgbParsed[1], 16),
    g: parseInt(rgbParsed[2], 16),
    b: parseInt(rgbParsed[3], 16)
  } : null;
  const rgba = opacityPercentage > 100 || opacityPercentage < 1 ?
    `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)` :
    `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacityPercentage / 100})`;

  return rgba;
};